// Small pure helpers for the storefront: finding a category from the address, and the short lines on cards.
import type { IconName } from "../../icons";
import { isIconName } from "../../icons";
import type { StoreCategory, StoreItem } from "./storeSite";

const LEDE = "Ranks, crate keys, and perks.";
/** The line under the store's title. It names only what can be paid with right now; before that is known it says nothing about it. */
export function storeLede(currencies?: { gems: boolean; orbs: boolean }) {
  if (!currencies) return LEDE;
  if (currencies.gems && currencies.orbs) return `${LEDE} Everything here is bought with Gems or Orbs.`;
  if (currencies.gems) return `${LEDE} Everything here is bought with Gems.`;
  if (currencies.orbs) return `${LEDE} Everything here is bought with Orbs.`;
  return `${LEDE} The store isn't taking orders right now.`;
}

type Sortable = { sortOrder: number; name: string };
export const bySort = (a: Sortable, b: Sortable) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);

export const childrenOf = (categories: StoreCategory[], parentId: number | null) => categories.filter((category) => category.parentId === parentId).sort(bySort);

/** The slugs after /store in an address ("/store/ranks/donor" gives ["ranks", "donor"]). */
export const slugsOf = (pathname: string) => pathname.replace(/^\/store\/?/, "").split("/").filter(Boolean);

/** The categories those slugs name, outermost first: [] for none, null when one of them does not exist. */
export function findChain(categories: StoreCategory[], slugs: string[]): StoreCategory[] | null {
  const chain: StoreCategory[] = [];
  let parent: number | null = null;
  for (const slug of slugs) {
    const next = categories.find((category) => category.parentId === parent && category.slug === slug);
    if (!next) return null;
    chain.push(next);
    parent = next.id;
  }
  return chain;
}

/** The chain that ends at a category, for building the address of an item or a sub-category. */
export function chainTo(categories: StoreCategory[], categoryId: number): StoreCategory[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const chain: StoreCategory[] = [];
  for (let current = byId.get(categoryId); current && chain.length <= categories.length; current = current.parentId == null ? undefined : byId.get(current.parentId)) chain.unshift(current);
  return chain;
}

export const pathOf = (chain: StoreCategory[]) => ["/store", ...chain.map((category) => category.slug)].join("/");
export const itemHref = (categories: StoreCategory[], item: StoreItem) => `${pathOf(chainTo(categories, item.categoryId))}?item=${encodeURIComponent(item.slug)}`;

export function typeLine(item: StoreItem) {
  if (item.rank) return `Rank ${item.rank.position} of ${item.rank.of} on the ${item.rank.ladder} ladder`;
  return item.type === "crate_key" ? "Crate key" : "Perk";
}

export const isSingle = (item: StoreItem) => item.type === "rank" || Boolean(item.rank);

/** "4 left" only when stock is low enough to matter. */
export function stockLine(item: StoreItem) {
  if (item.remainingStock == null || item.remainingStock > 10) return "";
  return item.remainingStock === 0 ? "Sold out" : `${item.remainingStock} left`;
}

export function endsIn(iso: string | null, now = Date.now()) {
  if (!iso) return "";
  const ms = Date.parse(iso) - now;
  if (!Number.isFinite(ms)) return "";
  if (ms <= 0) return "Ending now";
  const minutes = Math.ceil(ms / 60000), hours = Math.ceil(ms / 3600000), days = Math.ceil(ms / 86400000);
  if (minutes < 60) return `Ends in ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  if (hours < 24) return `Ends in ${hours} ${hours === 1 ? "hour" : "hours"}`;
  return `Ends in ${days} ${days === 1 ? "day" : "days"}`;
}

const TYPE_ICON: Record<string, IconName> = { rank: "star", crate_key: "crate", command: "spark", category: "folder" };
/** What to draw in an item's tile: its icon when that is one of ours, its icon text (an emoji) otherwise, else a glyph for its type. */
export function tileOf(icon: string | null, type: string | null): { name: IconName } | { text: string } {
  const value = (icon || "").trim();
  if (value && isIconName(value)) return { name: value };
  if (value) return { text: value };
  return { name: TYPE_ICON[type || ""] || "spark" };
}

/* ---------- Card copy, taken from the item's own description ---------- */

const plain = (text: string) => text.replace(/!\[[^\]]*\]\([^)]*\)/g, "").replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").replace(/[*_`~]/g, "").replace(/^#+\s*/, "").trim();
const BULLET = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/;

/** The first plain sentence line of an item's description, trimmed to fit a card. Bullets and headings are skipped. */
export function summaryOf(markdown: string, max = 110) {
  for (const line of markdown.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || BULLET.test(trimmed) || trimmed.startsWith("#")) continue;
    const text = plain(trimmed);
    if (!text) continue;
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
  }
  return "";
}

/** The bullet lines of an item's description, which is how staff list what a rank or perk includes. */
export function perksOf(markdown: string, limit = 6) {
  const perks: string[] = [];
  for (const line of markdown.split(/\r?\n/)) {
    const match = BULLET.exec(line);
    const text = match ? plain(match[1]) : "";
    if (text) perks.push(text.length > 80 ? `${text.slice(0, 79).trimEnd()}…` : text);
    if (perks.length >= limit) break;
  }
  return perks;
}

/** How much a running sale takes off, as a whole percent (the best of the item's sales), or 0 when nothing is on sale. */
export function savePercent(item: StoreItem) {
  let best = 0;
  for (const offer of item.offers) {
    if (!offer.saleActive || offer.listPrice <= 0 || offer.unitPrice >= offer.listPrice) continue;
    best = Math.max(best, Math.round((1 - offer.unitPrice / offer.listPrice) * 100));
  }
  return best;
}

/** The cheapest price anywhere under a category, in the first currency the store takes (Gems before Orbs). */
export function fromPrice(categories: StoreCategory[], items: StoreItem[], categoryId: number): { currency: "gems" | "orbs"; amount: number } | null {
  const inside = new Set<number>([categoryId]);
  for (let grew = true; grew;) {
    grew = false;
    for (const category of categories) if (category.parentId !== null && inside.has(category.parentId) && !inside.has(category.id)) { inside.add(category.id); grew = true; }
  }
  const offers = items.filter((item) => inside.has(item.categoryId)).flatMap((item) => item.offers);
  for (const currency of ["gems", "orbs"] as const) {
    const prices = offers.filter((offer) => offer.currency === currency).map((offer) => offer.unitPrice);
    if (prices.length) return { currency, amount: Math.min(...prices) };
  }
  return null;
}

/** The colour family of an item's art: gold for ranks, sky for crate keys, neutral for perks and categories. */
export const artKind = (type: string | null) => (type === "rank" ? "rank" : type === "crate_key" ? "key" : type === "category" ? "category" : "perk");
